import React from 'react';

export const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      title: 'Send events or messages',
      description: 'Instrument once via API or SDK. A single line of code unlocks everything.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
    },
    {
      number: '02',
      title: 'Automatic topic clustering',
      description: 'Messages are grouped by intent, not keywords. AI understands what users mean.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
    },
    {
      number: '03',
      title: 'Semantic journeys',
      description: 'See how users move between topics inside a session. Patterns emerge instantly.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
    },
  ];

  return (
    <section id="how-it-works" className="py-32 md:py-40 bg-white relative overflow-hidden">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50/50 via-white to-white" />
      
      <div className="section-container relative z-10">
        <div className="text-center mb-20">
          <p className="text-turret-purple font-semibold tracking-wide uppercase text-sm mb-4">
            Simple Integration
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 tracking-tight mb-6">
            How It Works
          </h2>
          <p className="text-xl text-neutral-500 max-w-xl mx-auto mb-8">
            Three steps to understand your AI conversations
          </p>
          
          {/* Code example - proves "single line" claim */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-turret-purple/20 to-turret-purple-light/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
              <div className="relative bg-neutral-900 rounded-2xl p-5 text-left overflow-x-auto">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  <span className="ml-2 text-xs text-neutral-500 font-mono">terminal</span>
                </div>
                <code className="text-sm font-mono">
                  <span className="text-emerald-400">curl</span>
                  <span className="text-neutral-300"> -X POST https://api.useturret.com/track \</span>
                  <br />
                  <span className="text-neutral-500">  </span>
                  <span className="text-sky-400">-H</span>
                  <span className="text-neutral-300"> </span>
                  <span className="text-amber-300">"X-API-Key: $API_KEY"</span>
                  <span className="text-neutral-300"> \</span>
                  <br />
                  <span className="text-neutral-500">  </span>
                  <span className="text-sky-400">-d</span>
                  <span className="text-neutral-300"> </span>
                  <span className="text-amber-300">{'\'{"name": "user_message", "metadata": {"prompt": "How do I reset my password?"}}\''}</span>
                </code>
              </div>
            </div>
          </div>

          {/* View Docs link - Apple style */}
          <a
            href="https://docs.useturret.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-turret-purple hover:text-turret-purple-light font-medium transition-all duration-300 group"
          >
            <span>View Documentation</span>
            <svg 
              className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <div key={index} className="group relative">
                <div className="relative h-full p-8 lg:p-10 rounded-3xl bg-gradient-to-b from-slate-50 to-white border border-slate-200/80 hover:border-turret-purple/30 hover:shadow-xl hover:shadow-turret-purple/5 transition-all duration-500">
                  {/* Step number - floating badge */}
                  <div className="absolute -top-3 -right-3 w-12 h-12 rounded-2xl bg-white border border-slate-200 shadow-lg flex items-center justify-center">
                    <span className="text-sm font-bold text-turret-purple">
                      {step.number}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-turret-purple to-turret-purple-light flex items-center justify-center text-white mb-8 shadow-lg shadow-turret-purple/25 group-hover:scale-110 transition-transform duration-500">
                    {step.icon}
                  </div>

                  <h3 className="text-xl font-semibold text-neutral-900 mb-4 tracking-tight">
                    {step.title}
                  </h3>

                  <p className="text-neutral-500 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Connection line for desktop */}
          <div className="hidden lg:block absolute top-1/2 translate-y-[20px] left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent -z-10" />
        </div>
      </div>
    </section>
  );
};
